<template>
  <div>
    <editor ref="editor" :input-name="inputName" :src-content="srcContent"
            @input="emitInput" @blur="$emit('blur')" @focus="$emit('focus')" @trix-attachment-add="handleAttachmentAdd"
            @trix-attachment-remove="handleAttachmentRemove" @trix-action-invoke="handleActionInvoke">
      <template #toolbar="{ toolbarId }">
        <toolbar :toolbar-id="toolbarId" :content-blocks="contentBlocks" :show-html-mode-toggle="showHtmlMode"
                 @change-to-html-mode="changeToHtmlMode">
          <template #content-insertion-component>
            <content-blocks-dropdown :content-blocks="contentBlocks" @insert="insertContentBlock" />
          </template>
        </toolbar>
      </template>
    </editor>
  </div>
</template>

<script>
import ajax from '@/helpers/ajax'
import ContentBlocksDropdown from './content-blocks-dropdown'
import Editor from './editor'
import Toolbar from './toolbar'
import Trix from 'trix'

const ATTACHMENT_KIND_CONTENT_BLOCK = 'contentBlock'

export default {
  components: { ContentBlocksDropdown, Editor, Toolbar },

  props: {
    contentBlocks: {
      type: Array,
      required: true
    },

    contentBlocksBasePath: {
      type: String,
      required: false
    },

    inputName: {
      type: String,
      required: false
    },

    showHtmlMode: {
      type: Boolean,
      required: false,
      default: true
    },

    srcContent: {
      type: String,
      required: false
    }
  },

  emits: ['blur', 'change-to-html-mode', 'focus', 'input', 'trix-attachment-add'],

  methods: {
    createContentBlockRecord({ kind, successCallback }) {
      // Tell the server that this particular instance of this content block is being inserted,
      // so it can create a record of it that has a persistent ID. We can then use the sgid we
      // get back to refer to this block later.
      ajax.post({
        url: this.contentBlocksBasePath,
        data: {
          content_block: {
            kind: kind
          }
        },
        successHandler: successCallback
      })
    },

    emitInput(newValue) {
      this.$emit('input', newValue)
    },

    focus() {
      this.$refs.editor.focus()
    },

    handleActionInvoke(evt) {
      // Our custom "Insert Content" button on the toolbar causes Trix to emit 'trix-action-invoke'
      // with the action 'x-content'.
      if (evt.actionName === 'x-content') {
        this.$refs.contentBlockSelector.open()
      }
    },

    handleAttachmentAdd(evt) {
      let attachment = evt.attachment

      if (attachment.getAttribute('kind') === ATTACHMENT_KIND_CONTENT_BLOCK && !attachment.getAttribute('sgid')) {
        // This happens when content is loaded into the editor that includes a contentBlock attachment without an sgid.
        // For example, this can happen when certain email template content is applied.
        // Do the appropriate process to obtain an sgid and add it to the attachment.
        this.createContentBlockRecord({
          kind: attachment.getAttribute('contentBlockKind'),
          successCallback: ({ sgid }) => {
            attachment.setAttributes({sgid: sgid})
          }
        })
      }

      // Re-emit the event for anything else that might want to handle it
      this.$emit('trix-attachment-add', evt)
    },

    handleAttachmentRemove(evt) {
      const attachment = evt.attachment

      // If a content block is removed from the body, tell the server so it can delete its corresponding record.
      if (attachment.getAttribute('kind') === ATTACHMENT_KIND_CONTENT_BLOCK) {
        const sgid = attachment.getAttribute('sgid')
        ajax.delete({
          url: `${this.contentBlocksBasePath}/${sgid}`
        })
      }
    },

    insertContentBlock(contentBlock) {
      // Tell the server so we get an sgid for this content block, then insert it via the Trix Editor interface.
      this.createContentBlockRecord({
        kind: contentBlock.kind,
        successCallback: ({ sgid }) => {
          const attachment = new Trix.Attachment({
            content: contentBlock.content,
            kind: ATTACHMENT_KIND_CONTENT_BLOCK,
            sgid: sgid
          })

          this.$refs.editor.insertAttachment(attachment)
        }
      })
    },

    changeToHtmlMode() {
      this.$emit('change-to-html-mode')
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep(.trix-content) {
  .attachment--content {
    width: 100%;
  }
}
</style>
